/**
 * SimonCat Agent Platform - Knowledge Base API Client
 *
 * Backend endpoints:
 * POST   /api/knowledge/upload        Upload document
 * GET    /api/knowledge               List user documents
 * GET    /api/knowledge/search        Search knowledge base
 * GET    /api/knowledge/{docId}       Get document details
 * DELETE /api/knowledge/{docId}       Delete document
 */

import axios from 'axios';

/* ---------------------------------------------------------------------------
 * Display constants for KnowledgeCard component
 * ------------------------------------------------------------------------- */

export interface KnowledgeTypeConfig {
  label: string;
  color: string;
}

export const KNOWLEDGE_TYPES: Record<string, KnowledgeTypeConfig> = {
  document: { label: '📄 文档', color: 'blue' },
  faq: { label: '❓ FAQ', color: 'cyan' },
  policy: { label: '📋 政策', color: 'purple' },
  guide: { label: '📖 指南', color: 'green' },
  api_doc: { label: '🔧 API文档', color: 'orange' },
  tutorial: { label: '🎓 教程', color: 'magenta' },
  legal: { label: '⚖️ 法律', color: 'gold' },
  other: { label: '📎 其他', color: 'default' },
};

export interface CountryConfig {
  code: string;
  label: string;
}

export const COUNTRIES: CountryConfig[] = [
  { code: 'CN', label: '🇨🇳 中国' },
  { code: 'US', label: '🇺🇸 美国' },
  { code: 'JP', label: '🇯🇵 日本' },
  { code: 'KR', label: '🇰🇷 韩国' },
  { code: 'DE', label: '🇩🇪 德国' },
  { code: 'FR', label: '🇫🇷 法国' },
  { code: 'GB', label: '🇬🇧 英国' },
  { code: 'SG', label: '🇸🇬 新加坡' },
  { code: 'AU', label: '🇦🇺 澳大利亚' },
  { code: 'CA', label: '🇨🇦 加拿大' },
];

const API_BASE = '/api/knowledge';

export interface KnowledgeDocumentDTO {
  id?: number;
  docId: string;
  name: string;
  fileType: string;
  fileSize: number;
  status: 'PROCESSING' | 'READY' | 'ERROR';
  chunkCount: number;
  totalTokens: number;
  summary?: string;
  createdBy?: number;
  agentId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SearchResult<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  tookMs?: number;
}

/**
 * Upload a document to the knowledge base
 */
export async function uploadDocument(
  file: File,
  agentId?: string
): Promise<KnowledgeDocumentDTO> {
  const formData = new FormData();
  formData.append('file', file);
  if (agentId) {
    formData.append('agentId', agentId);
  }

  const resp = await axios.post<KnowledgeDocumentDTO>(`${API_BASE}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resp.data;
}

/**
 * Get user's document list
 */
export async function fetchKnowledgeDocuments(params?: {
  page?: number;
  size?: number;
}): Promise<SearchResult<KnowledgeDocumentDTO>> {
  const resp = await axios.get<SearchResult<KnowledgeDocumentDTO>>(API_BASE, { params });
  return resp.data;
}

/**
 * Search knowledge base
 */
export async function searchKnowledge(
  query: string,
  params?: { page?: number; size?: number }
): Promise<SearchResult<KnowledgeDocumentDTO>> {
  const resp = await axios.get<SearchResult<KnowledgeDocumentDTO>>(`${API_BASE}/search`, {
    params: { q: query, ...params },
  });
  return resp.data;
}

/**
 * Get document details
 */
export async function fetchKnowledgeDocument(docId: string): Promise<KnowledgeDocumentDTO> {
  const resp = await axios.get<KnowledgeDocumentDTO>(`${API_BASE}/${docId}`);
  return resp.data;
}

/**
 * Delete a document
 */
export async function deleteKnowledgeDocument(docId: string): Promise<boolean> {
  const resp = await axios.delete<boolean>(`${API_BASE}/${docId}`);
  return resp.data;
}

/**
 * Legacy compatibility export (named) for KnowledgeBase page
 */
export const knowledgeAPI = {
  uploadDocument,
  fetchKnowledgeDocuments,
  searchKnowledge,
  fetchKnowledgeDocument,
  deleteKnowledgeDocument,
  KNOWLEDGE_TYPES,
  COUNTRIES,
};
